import React from 'react';
import { shootDays, shootRange } from '../../utils/clientDates';
import type { Shoot } from '../../types/portal';

/**
 * BLOCK 2, WHEN THERE IS NOTHING TO WATCH YET.
 *
 * The client page is four blocks and only the second one changes (see
 * pages/client/ProjectPage.tsx). This is the version a project wears
 * before any work exists: Marjan, today — contract signed, recce on the
 * 13th, nothing filmed.
 *
 * THE DATE IS THE PAGE. Tom, 8 Oct: *"I don't want a massive long page
 * that they have to scroll down."* At this stage there is exactly one
 * useful fact and it gets the display type. The two dates after it are
 * quiet lines, and everything beyond that lives behind "Full schedule" in
 * the foot of the page rather than on it.
 *
 * WITH NO DATES AT ALL it falls back to the derived status sentence —
 * "Editing: 4 deliverables." — which is true and short. It is never left
 * blank: a client who opens their link and finds an empty page assumes
 * the page is broken, not that the project is quiet.
 */
export function LeadDate({
  shoots,
  status,
  support
}: {
  shoots: Shoot[];
  status: string;
  support: string;
}) {
  const next = shoots[0];
  /* Two, then the link. A third quiet date is a schedule, and the schedule
     has its own place. */
  const rest = shoots.slice(1, 3);
  if (!next) {
    return <div className="mt-12" data-id="element-110" data-mp="1dowjbh7qg7xg">
        <p className="label m-0 text-mute" data-id="element-111" data-mp="1draavg37f209">Where we are</p>
        <p className="mt-6 m-0 max-w-[32ch] font-display text-3xl font-light leading-snug sm:text-[2.5rem]" data-id="element-112" data-mp="g2z35qgtseoo">
          {status}
        </p>
        {support && <p className="mt-5 m-0 max-w-[48ch] text-[1.05rem] leading-relaxed text-graphite" data-id="element-113" data-mp="pzbystvxl2m2">
            {support}
          </p>}
      </div>;
  }
  const detail = [shootDays(next), next.time, next.location || next.city].filter(Boolean).join(' · ');
  return <div className="mt-12" data-id="element-114" data-mp="26e3dk405mbcw">
      <p className="label m-0 text-mute" data-id="element-115" data-mp="26dbu1rju042z">Next up</p>
      <p className="mt-6 m-0 font-display text-5xl leading-[0.9] tracking-tight sm:text-7xl" data-id="element-116" data-mp="26g7qfbg16kpb">
        {shootRange(next)}
      </p>
      {next.title && <p className="mt-7 m-0 font-display text-2xl font-light leading-snug sm:text-3xl" data-id="element-117" data-mp="1bpo8to1rk5b">
          {next.title}
        </p>}
      {detail && <p className="mt-4 m-0 text-[1.05rem] leading-relaxed text-graphite" data-id="element-118" data-mp="bayp86hbxz16">{detail}</p>}

      {rest.length > 0 && <ul className="m-0 mt-10 list-none border-t border-rule p-0" data-id="element-119" data-mp="3jq8jwtiw41yx">
          {rest.map(shoot => <li key={shoot.id} className="flex flex-wrap items-baseline gap-x-6 gap-y-2 border-b border-rule py-5" data-id="element-120" data-mp="3esgkzfjtvfl8">
              <span className="font-display text-xl leading-none sm:text-2xl" data-id="element-121" data-mp="3skwt2url63t0">
                {shootRange(shoot)}
              </span>
              <span className="text-[1rem] text-graphite" data-id="element-122" data-mp="301zjxxb3urov">
                {[shoot.title, shoot.location || shoot.city].filter(Boolean).join(' · ')}
              </span>
            </li>)}
        </ul>}

      {support && <p className="mt-9 m-0 max-w-[48ch] text-[1.05rem] leading-relaxed text-graphite" data-id="element-123" data-mp="2lvxvvvw1dkh7">
          {support}
        </p>}
    </div>;
}